import { type KeyboardEvent, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid, type GridPaginationModel } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { Dayjs } from 'dayjs';
import type { Problem, SessionSearchItem, UISessionsSearchByIpParams } from '../../api/model';
import { useUISessionsSearchByIp } from '../../api/sessions-ui/sessions-ui.ts';
import {
  buildSearchTimeWindow,
  isAllTimeSearchWindow,
  SEARCH_TIME_PRESET_LABELS,
  validateSearchTimeWindow,
  type SearchTimePreset,
} from './buildSearchTimeWindow';
import { sessionSearchColumns } from './sessionSearchColumns';
import { customUtcDaysToApiWindow, dayjsUtcFromApiIso } from './utcDateRange';

const DEFAULT_PAGE_SIZE = 25;

const datePickerSlotProps = {
  textField: { size: 'small' as const, sx: { width: 180 } },
};

type SessionSearchRow = SessionSearchItem & { id: string };

type ActiveSearch = Pick<UISessionsSearchByIpParams, 'ip' | 'from' | 'to'>;

const defaultCustomRange = () => {
  const { from, to } = buildSearchTimeWindow('24h');

  return {
    from: dayjsUtcFromApiIso(from),
    to: dayjsUtcFromApiIso(to),
  };
};

const getSessionSearchError = (error: Problem) => {
  if (!error) {
    return '';
  }
  const status = error.response?.status;
  if (status === 401) {
    return 'Unauthorized access';
  }
  if (status === 400) {
    return error.response?.data?.detail ?? 'Invalid search parameters';
  }
  return error.response?.data?.detail ?? 'An error occurred while searching sessions';
};

const formatUtcDateLabel = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).format(new Date(iso));

export const BngIpLookupPage = () => {
  const initialCustomRange = defaultCustomRange();
  const [ipInput, setIpInput] = useState('');
  const [preset, setPreset] = useState<SearchTimePreset>('24h');
  const [customFrom, setCustomFrom] = useState<Dayjs>(initialCustomRange.from);
  const [customTo, setCustomTo] = useState<Dayjs>(initialCustomRange.to);
  const [formError, setFormError] = useState<string | null>(null);
  const [activeSearch, setActiveSearch] = useState<ActiveSearch | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const queryParams: UISessionsSearchByIpParams | null = activeSearch
    ? {
        ...activeSearch,
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
      }
    : null;

  const { data, error, isFetching } = useUISessionsSearchByIp(queryParams ?? { ip: '', from: '', to: '' }, {
    query: {
      enabled: Boolean(queryParams),
    },
  });

  const rows = useMemo<SessionSearchRow[]>(
    () =>
      (data?.data ?? []).map((row, index) => ({
        ...row,
        id: `${row.username}-${row.acct_start_time}-${row.matched_by}-${index}`,
      })),
    [data?.data],
  );

  const handlePresetChange = (nextPreset: SearchTimePreset) => {
    setPreset(nextPreset);
    setFormError(null);

    if (nextPreset === 'custom') {
      return;
    }

    const { from, to } = buildSearchTimeWindow(nextPreset);
    setCustomFrom(dayjsUtcFromApiIso(from));
    setCustomTo(dayjsUtcFromApiIso(to));
  };

  const resolveSearchWindow = (): { from: string; to: string } | null => {
    if (preset === 'custom') {
      if (!customFrom?.isValid() || !customTo?.isValid()) {
        return null;
      }

      const { from, to } = customUtcDaysToApiWindow(customFrom, customTo);
      const validationError = validateSearchTimeWindow(from, to);

      if (validationError) {
        setFormError(validationError);
        return null;
      }

      return { from, to };
    }

    return buildSearchTimeWindow(preset);
  };

  const handleSearch = () => {
    const trimmedIp = ipInput.trim();
    if (!trimmedIp) {
      setFormError('Enter an IP address');
      return;
    }

    setFormError(null);
    const window = resolveSearchWindow();

    if (!window) {
      if (preset === 'custom') {
        setFormError((current) => current ?? 'Select a valid From and To date');
      }
      return;
    }

    setPaginationModel((current) => ({ ...current, page: 0 }));
    setActiveSearch({ ip: trimmedIp, from: window.from, to: window.to });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const hasSearched = activeSearch !== null;
  const showEmptyResults = hasSearched && !isFetching && !error && rows.length === 0;
  const totalRows = data?.meta?.pagination?.total ?? 0;
  const isCustomRange = preset === 'custom';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ maxWidth: 1400, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          BNG IP Lookup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Find subscribers whose BNG session had this IP assigned while the session overlapped your selected
          period.
        </Typography>

        <Card sx={{ mb: 3, flexShrink: 0 }}>
          <CardContent>
            <Stack spacing={1}>
              {formError && (
                <Alert severity="error" sx={{ py: 0 }}>
                  {formError}
                </Alert>
              )}
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <TextField
                    size="small"
                    label="IP address"
                    placeholder="IPv4 or IPv6 assigned on the BNG..."
                    value={ipInput}
                    onChange={(e) => setIpInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{ minWidth: 220, flex: 1 }}
                  />
                  <FormControl size="small" sx={{ width: 165, flexShrink: 0 }}>
                    <InputLabel id="bng-ip-lookup-preset-label">Period</InputLabel>
                    <Select
                      labelId="bng-ip-lookup-preset-label"
                      label="Period"
                      value={preset}
                      onChange={(e) => handlePresetChange(e.target.value as SearchTimePreset)}
                    >
                      {(Object.keys(SEARCH_TIME_PRESET_LABELS) as SearchTimePreset[]).map((key) => (
                        <MenuItem key={key} value={key}>
                          {SEARCH_TIME_PRESET_LABELS[key]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton onClick={handleSearch} aria-label="search" color="primary" size="small">
                    <SearchIcon />
                  </IconButton>
                </Stack>
                {isCustomRange && (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <DatePicker
                      label="From (UTC)"
                      value={customFrom}
                      onChange={(value) => {
                        if (value) {
                          setCustomFrom(value);
                          setFormError(null);
                        }
                      }}
                      timezone="UTC"
                      maxDate={customTo}
                      slotProps={datePickerSlotProps}
                    />
                    <DatePicker
                      label="To (UTC)"
                      value={customTo}
                      onChange={(value) => {
                        if (value) {
                          setCustomTo(value);
                          setFormError(null);
                        }
                      }}
                      timezone="UTC"
                      minDate={customFrom}
                      slotProps={datePickerSlotProps}
                    />
                  </Stack>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Press Enter to search. A session counts if it overlapped the period and had this IP assigned
                (including still-active sessions). Custom range uses full UTC calendar days.
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {!hasSearched && (
          <Alert severity="info">
            Enter an IP and period to list BNG sessions where that address was assigned during the overlap
            window.
          </Alert>
        )}

        {hasSearched && activeSearch && data?.meta && (
          <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                BNG sessions where <strong>{activeSearch.ip}</strong> ({data.meta.ip_version}) was assigned
                and overlapped{' '}
                {isAllTimeSearchWindow(activeSearch.from) ? (
                  <strong>all recorded history</strong>
                ) : (
                  <>
                    <strong>{formatUtcDateLabel(activeSearch.from)}</strong>–
                    <strong>{formatUtcDateLabel(activeSearch.to)}</strong> (UTC)
                  </>
                )}
              </Typography>
              {data.meta.searched_ipv6_prefix && (
                <Typography variant="body2">
                  Derived IPv6 /56 prefix: <strong>{data.meta.searched_ipv6_prefix}</strong>
                </Typography>
              )}
            </Stack>
          </Alert>
        )}

        {error && !isFetching && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
            {getSessionSearchError(error)}
          </Alert>
        )}

        {showEmptyResults && (
          <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
            No BNG sessions had this IP assigned during the selected period
          </Alert>
        )}

        {hasSearched && (
          <DataGrid
            rows={rows}
            columns={sessionSearchColumns}
            loading={isFetching}
            paginationMode="server"
            rowCount={totalRows}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              flex: 1,
              minHeight: 400,
              '& .MuiDataGrid-cell': {
                alignItems: 'center',
              },
            }}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};
